import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { io as ioClient } from "socket.io-client";
import api, { API_ORIGIN, assetUrl } from "../api";
import { filterFiles, isAttachmentFile, MAX_ATTACHMENTS, uploadErrorMessage } from "../uploadUtils";
import AdminLayout from "../components/AdminLayout";
import {
  Home, Search, Filter, MoreVertical, Paperclip, Send, Pencil, Check, ArrowRight, FileText,
} from "lucide-react";


const full = assetUrl;
const money = (n) => `$${(Number(n) || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const initials = (name) => (name?.[0] || "?").toUpperCase();

const fmtListTime = (d) => {
  const date = new Date(d);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) return date.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  const days = Math.round((now - date) / 86400000);
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
};
const fmtBubbleTime = (d) => new Date(d).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });

const TABS = ["All", "Unread", "Open", "Closed"];

export default function CommentsInbox() {
  const navigate = useNavigate();

  const [conversations, setConversations] = useState([]);
  const [tab, setTab] = useState("Unread");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null); // requestDocId

  const [detail, setDetail] = useState(null); // full Request doc for selected conversation
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");

  const fileRef = useRef(null);
  const threadEndRef = useRef(null);
  const socketRef = useRef(null);
  const joinedRoomRef = useRef(null);

  // ---------- load conversation list ----------
  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/conversations");
      setConversations(Array.isArray(data) ? data : []);
    } catch {
      /* leave list as-is; polling/socket will retry */
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // polling fallback — safety net if the socket connection drops
  useEffect(() => {
    const t = setInterval(loadConversations, 20000);
    return () => clearInterval(t);
  }, [loadConversations]);

  // ---------- socket setup (once) ----------
  useEffect(() => {
    const socket = ioClient(API_ORIGIN, { transports: ["websocket", "polling"] });
    socketRef.current = socket;
    socket.emit("join", "admin");

    socket.on("conversation:update", (payload) => {
      setConversations((prev) => {
        const idx = prev.findIndex((c) => String(c.requestDocId) === String(payload.requestId));
        if (idx === -1) {
          loadConversations();
          return prev;
        }
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          lastText: payload.lastText ?? next[idx].lastText,
          lastAt: payload.lastAt ?? next[idx].lastAt,
          lastAuthorRole: payload.lastAuthorRole ?? next[idx].lastAuthorRole,
          unreadCount: payload.unreadCount ?? next[idx].unreadCount,
        };
        next.sort((a, b) => new Date(b.lastAt) - new Date(a.lastAt));
        return next;
      });
    });

    return () => socket.disconnect();
  }, [loadConversations]);

  // ---------- join/leave the per-thread room as selection changes ----------
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !selectedId) return;
    if (joinedRoomRef.current) socket.emit("leave", joinedRoomRef.current);
    const room = `request:${selectedId}`;
    socket.emit("join", room);
    joinedRoomRef.current = room;

    const onNew = (comment) => {
      if (String(comment.requestId) !== String(selectedId)) return;
      setMessages((prev) => (prev.some((m) => m._id === comment._id) ? prev : [...prev, comment]));
    };
    socket.on("comment:new", onNew);
    return () => socket.off("comment:new", onNew);
  }, [selectedId]);

  useEffect(() => { threadEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // ---------- load a thread's detail + messages, mark read ----------
  const openConversation = async (c) => {
    setSelectedId(c.requestDocId);
    setDetail(null);
    setMessages([]);
    setEditingNote(false);

    try {
      const [r, m] = await Promise.all([
        api.get(`/requests/${c.requestDocId}`),
        api.get(`/requests/${c.requestDocId}/comments`),
      ]);
      setDetail(r.data);
      setMessages(m.data);
      setNoteDraft(r.data.quoteInternalNote || "");
    } catch {
      /* ignore */
    }

    if (c.unreadCount > 0) {
      setConversations((prev) =>
        prev.map((x) => (x.requestDocId === c.requestDocId ? { ...x, unreadCount: 0 } : x))
      );
      api.patch(`/admin/conversations/${c.requestDocId}/read`).catch(() => { });
    }
  };

  const addFiles = (list) => {
    const { accepted, problems } = filterFiles(list, { room: MAX_ATTACHMENTS - files.length, accept: isAttachmentFile });
    if (problems.length) alert(problems.join("\n"));
    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
  };

  const send = async () => {
    if (sending || (!text.trim() && files.length === 0) || !selectedId) return;
    setSending(true);
    const fd = new FormData();
    fd.append("text", text);
    files.forEach((f) => fd.append("attachments", f));
    try {
      const { data } = await api.post(`/requests/${selectedId}/comments`, fd);
      if (data?._id) setMessages((prev) => (prev.some((m) => m._id === data._id) ? prev : [...prev, data]));
      setText("");
      setFiles([]);
    } catch (err) {
      alert(uploadErrorMessage(err, "Could not send."));
    } finally {
      setSending(false);
    }
  };

  const saveNote = async () => {
    try {
      await api.patch(`/requests/${selectedId}/internal-note`, { note: noteDraft });
      setDetail((d) => ({ ...d, quoteInternalNote: noteDraft }));
      setEditingNote(false);
    } catch {
      alert("Could not save note.");
    }
  };

  // ---------- filtering the left list ----------
  const visible = useMemo(() => {
    let list = conversations;
    if (tab === "Unread") list = list.filter((c) => c.unreadCount > 0);
    else if (tab === "Open") list = list.filter((c) => !["Approved", "Closed"].includes(c.status));
    else if (tab === "Closed") list = list.filter((c) => c.status === "Closed");

    const q = query.trim().toLowerCase();
    if (q) list = list.filter((c) => [c.customerName, c.requestId, c.lastText].filter(Boolean).some((v) => v.toLowerCase().includes(q)));
    return list;
  }, [conversations, tab, query]);

  const unreadTotal = conversations.reduce((s, c) => s + (c.unreadCount || 0), 0);
  const selectedSummary = conversations.find((c) => c.requestDocId === selectedId);
  const sharedPhotos = detail ? [detail.vehicleImage, ...(detail.partPhotos || [])].filter(Boolean) : [];
  const vehicle = detail?.quoteParts?.[0]?.vehicle || detail?.vin || detail?.rego || "—";
  const part = detail?.quoteParts?.[0]?.name || detail?.partRequired || "—";

  return (
    <AdminLayout>
      <main className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-extrabold text-slate-900">Comments</h1>
          <div className="flex items-center gap-1.5 text-sm text-slate-400 mt-1">
            <Home size={14} />
            <Link to="/admin/dashboard" className="hover:text-slate-600">Dashboard</Link>
            <span>›</span>
            <span className="text-slate-500">Comments</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr_300px] gap-6 items-start">
          {/* ---------- conversation list ---------- */}
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2 mb-3">
              <Search size={15} className="text-slate-400" />
              <input value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search conversations..."
                className="flex-1 text-sm focus:outline-none placeholder:text-slate-400" />
              <Filter size={15} className="text-slate-400" />
            </div>

            <div className="flex items-center gap-1 mb-3 text-sm">
              {TABS.map((t) => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-2.5 py-1.5 rounded-lg font-semibold flex items-center gap-1 ${tab === t ? "text-slate-900 border-b-2 border-slate-900" : "text-slate-400"
                    }`}>
                  {t}
                  {t === "Unread" && unreadTotal > 0 && (
                    <span className="bg-teal-600 text-white text-[10px] font-bold rounded-full w-4.5 h-4.5 px-1 grid place-items-center">
                      {unreadTotal}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="space-y-1 max-h-[600px] overflow-y-auto">
              {visible.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-8">No conversations here.</p>
              )}
              {visible.map((c) => (
                <button key={c.requestDocId} onClick={() => openConversation(c)}
                  className={`w-full flex items-start gap-3 p-3 rounded-xl text-left ${selectedId === c.requestDocId ? "bg-slate-100" : "hover:bg-slate-50"
                    } ${selectedId === c.requestDocId ? "border-l-4 border-teal-600" : "border-l-4 border-transparent"}`}>
                  <span className="grid place-items-center w-9 h-9 rounded-full bg-slate-700 text-white text-xs font-bold shrink-0">
                    {initials(c.customerName)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-slate-800 text-sm truncate">{c.customerName || "Unknown"}</span>
                      <span className="text-[11px] text-slate-400 shrink-0">{fmtListTime(c.lastAt)}</span>
                    </div>
                    <p className="text-xs text-slate-500 truncate">{c.lastText || "Attachment"}</p>
                  </div>
                  {c.unreadCount > 0 && (
                    <span className="bg-teal-600 text-white text-[10px] font-bold rounded-full w-5 h-5 grid place-items-center shrink-0">
                      {c.unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* ---------- thread ---------- */}
          <div className="bg-white rounded-2xl shadow-sm flex flex-col h-[720px]">
            {!selectedId ? (
              <div className="flex-1 grid place-items-center text-slate-400 text-sm">
                Select a conversation to view messages.
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
                  <span className="grid place-items-center w-9 h-9 rounded-full bg-slate-700 text-white text-xs font-bold">
                    {initials(selectedSummary?.customerName)}
                  </span>
                  <div>
                    <div className="font-bold text-slate-800">{selectedSummary?.customerName}</div>
                    <div className="text-xs text-slate-400">Request ID: {selectedSummary?.requestId}</div>
                  </div>
                  <button onClick={() => navigate(`/admin/requests/${selectedId}`)}
                    className="ml-auto border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    View Request
                  </button>
                  <button className="text-slate-300"><MoreVertical size={18} /></button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                  {messages.map((m) => {
                    const mine = m.authorRole === "admin";
                    return (
                      <div key={m._id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[70%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                          <div className={`px-4 py-2.5 rounded-2xl text-sm ${mine ? "bg-blue-50 text-slate-800" : "bg-slate-100 text-slate-700"
                            }`}>
                            {m.text}
                          </div>
                          {m.attachments?.length > 0 && (
                            <div className="mt-2 space-y-1.5">
                              {m.attachments.map((a, i) => (
                                a.type === "image" ? (
                                  <img key={i} src={full(a.url)} alt={a.name} className="max-w-[200px] rounded-xl border" />
                                ) : (
                                  <a key={i} href={full(a.url)} download target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-2 p-2 rounded-lg border border-gray-100 text-xs">
                                    <FileText size={14} className="text-red-500" /> {a.name}
                                  </a>
                                )
                              ))}
                            </div>
                          )}
                          <span className="text-[10px] text-slate-400 mt-1">
                            {mine && (m.authorName || "Staff")}
                            {mine && m.staffRole && ` (${m.staffRole === "admin" ? "Admin" : "Worker"})`}
                            {mine && " · "}{fmtBubbleTime(m.createdAt)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={threadEndRef} />
                </div>

                {files.length > 0 && (
                  <div className="px-6 flex flex-wrap gap-2">
                    {files.map((f, i) => (
                      <span key={i} className="text-xs bg-slate-100 rounded-full px-3 py-1 inline-flex items-center gap-1">{f.name}<button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}>×</button></span>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2 px-6 py-4 border-t border-gray-100">
                  <input value={text} onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && !sending && send()}
                    placeholder="Type a message..."
                    className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none" />
                  <button onClick={() => fileRef.current?.click()} className="text-slate-400 hover:text-slate-600">
                    <Paperclip size={18} />
                  </button>
                  <input ref={fileRef} type="file" multiple hidden
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                  <button onClick={send} disabled={sending}
                    className="w-11 h-11 grid place-items-center rounded-xl bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-60">
                    <Send size={17} />
                  </button>
                </div>
              </>
            )}
          </div>

          {/* ---------- customer & request panel ---------- */}
          {selectedId && detail && (
            <div className="bg-white rounded-2xl shadow-sm p-5 space-y-5">
              <div>
                <h3 className="font-bold text-slate-800 mb-3">Customer &amp; Request</h3>
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-10 h-10 rounded-full bg-slate-700 text-white text-sm font-bold">
                    {initials(detail.customerName)}
                  </span>
                  <div>
                    <div className="font-bold text-slate-800 text-sm">{detail.customerName}</div>
                    <div className="text-xs text-slate-400">{detail.customerContact}</div>
                  </div>
                </div>
              </div>

              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-[10px] text-slate-400 uppercase font-semibold">Request ID</div>
                  <div className="font-bold text-slate-800">{detail.requestId}</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-400 uppercase font-semibold">Vehicle</div>
                  <div className="font-bold text-slate-800">{vehicle}</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-400 uppercase font-semibold">Part Required</div>
                  <div className="font-bold text-slate-800">{part}</div>
                </div>
                <button onClick={() => navigate(`/admin/requests/${selectedId}`)}
                  className="flex items-center gap-1 text-blue-700 text-sm font-semibold">
                  View Request <ArrowRight size={14} />
                </button>
              </div>

              {sharedPhotos.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-bold text-slate-800">Shared Photos</h4>
                    <button onClick={() => navigate(`/admin/requests/${selectedId}`)} className="text-xs text-blue-700 font-semibold">
                      View all
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {sharedPhotos.slice(0, 3).map((p, i) => (
                      <img key={i} src={full(p)} alt="" className="w-full h-16 object-cover rounded-lg" />
                    ))}
                    {sharedPhotos.length > 3 && (
                      <div className="w-full h-16 rounded-lg bg-slate-100 grid place-items-center text-xs font-semibold text-slate-500">
                        +{sharedPhotos.length - 3}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {detail.quoteId && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-bold text-slate-800">Recent Quotes</h4>
                    <button onClick={() => navigate(`/admin/requests/${selectedId}`)} className="text-xs text-blue-700 font-semibold">
                      View all
                    </button>
                  </div>
                  <div className="border border-gray-100 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-800 text-sm">{detail.quoteId}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        {detail.quoteStatus === "sent" ? "Sent" : "Draft"}
                      </span>
                    </div>
                    <div className="text-lg font-extrabold text-slate-800 mt-1">
                      {detail.quotePricing?.total != null ? money(detail.quotePricing.total) : "—"}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-bold text-slate-800">Notes</h4>
                  {!editingNote ? (
                    <button onClick={() => setEditingNote(true)} className="text-xs text-blue-700 font-semibold flex items-center gap-1">
                      <Pencil size={12} /> Edit
                    </button>
                  ) : (
                    <button onClick={saveNote} className="text-xs text-green-700 font-semibold flex items-center gap-1">
                      <Check size={12} /> Save
                    </button>
                  )}
                </div>
                {editingNote ? (
                  <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)}
                    rows={3} className="w-full border border-gray-200 rounded-lg p-2 text-sm focus:outline-none" />
                ) : (
                  <p className="text-sm text-slate-500 italic">{detail.quoteInternalNote || "No notes yet."}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </AdminLayout>
  );
}
